/**
 * Чат: React Context и склейка хуков. Модули — ./chat/AGENT.md; карта всего репо — ../../../../AGENT.md
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react";
import type { MessageRow } from "../../lib/db";
import type { ServerMessageRow } from "../../lib/authApi";
import {
  conversationCapabilitiesFor,
  type ConversationCapabilities,
} from "../state/conversationCapabilities";
import {
  chatReducer,
  initialChatState,
  type ChatAction,
  type ChatState,
  type ConversationRef,
} from "../state/chatState";
import { useSessionResetRegistry } from "../SessionResetContext";
import { useAuth } from "./AuthContext";
import { useContacts } from "./ContactsContext";
import { useRealtime } from "./RealtimeContext";
import { useChatConversationEffects } from "./chat/useChatConversationEffects";
import { useChatMessageLoading } from "./chat/useChatMessageLoading";
import { useChatSendPipeline } from "./chat/useChatSendPipeline";
import { useDecodeServerMessageRow } from "./chat/useDecodeServerMessageRow";
import { useDmSync } from "./chat/useDmSync";
import { useOutboxFlush } from "./chat/useOutboxFlush";

export type ChatContextValue = {
  chatState: ChatState;
  conversationCapabilities: ConversationCapabilities;
  dispatchChat: React.Dispatch<ChatAction>;
  conversationLoading: boolean;
  loadingOlder: boolean;
  selectedConversationRef: React.MutableRefObject<ConversationRef | null>;
  notifyMessagesChanged: (peerId: string) => void;
  loadPeerMessagesIntoState: (peerId: string, options?: { keepLoadingWhenEmpty?: boolean }) => Promise<void>;
  loadOlderChatMessages: () => Promise<void>;
  decodeServerMessageRow: (row: ServerMessageRow) => Promise<MessageRow | null>;
  mergeServerHistory: (peerId: string) => Promise<void>;
  repairSync: () => Promise<void>;
  forwardMessageToConversation: (
    message: MessageRow,
    sourceConversation: ConversationRef,
    targetConversation: ConversationRef,
  ) => Promise<boolean>;
  sendPlaintextMessage: (peerId: string, plaintext: string) => Promise<boolean>;
  sendChat: () => Promise<void>;
  toggleReaction: (message: MessageRow, emoji: string, remove: boolean) => Promise<void>;
  deleteMessage: (message: MessageRow) => Promise<void>;
  pickChatImage: (file: File | null) => Promise<void>;
  pickChatFile: (file: File | null) => Promise<void>;
  removeChatImage: () => void;
  retryFailedOutboxSend: (messageId: string) => Promise<void>;
  sendVoiceMessage: (plainBytes: Uint8Array, mime: string, durationMs: number, waveform: number[]) => Promise<void>;
};

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const { register } = useSessionResetRegistry();
  const { keysRef, identityRow, keys } = useAuth();
  const { refreshUnreadCounts } = useContacts();
  const { sendRealtimeCommand, authenticated, conn } = useRealtime();

  const [chatState, dispatchChat] = useReducer(chatReducer, undefined, initialChatState);
  const selectedConversationRef = useRef<ConversationRef | null>(null);

  useEffect(() => {
    return register(() => {
      dispatchChat({ type: "reset" });
    });
  }, [register]);

  useEffect(() => {
    selectedConversationRef.current = chatState.selectedConversation;
  }, [chatState.selectedConversation]);

  const decodeServerMessageRow = useDecodeServerMessageRow(keysRef, selectedConversationRef);

  const { notifyMessagesChanged, mergeServerHistory, repairSync } = useDmSync(
    keysRef,
    selectedConversationRef,
    sendRealtimeCommand,
    decodeServerMessageRow,
    identityRow,
    keys,
    authenticated,
    conn,
  );

  const { loadingOlder, loadPeerMessagesIntoState, loadOlderChatMessages } = useChatMessageLoading(
    chatState,
    dispatchChat,
    refreshUnreadCounts,
    sendRealtimeCommand,
    selectedConversationRef,
    keysRef,
    decodeServerMessageRow,
  );

  useChatConversationEffects(
    chatState,
    dispatchChat,
    authenticated,
    selectedConversationRef,
    repairSync,
    mergeServerHistory,
    loadPeerMessagesIntoState,
    decodeServerMessageRow,
    notifyMessagesChanged,
    identityRow?.userId ?? null,
    keysRef,
  );

  const {
    forwardMessageToConversation,
    sendPlaintextMessage,
    sendChat,
    toggleReaction,
    deleteMessage,
    pickChatImage,
    pickChatFile,
    removeChatImage,
    retryFailedOutboxSend,
    sendVoiceMessage,
  } = useChatSendPipeline(
    chatState,
    dispatchChat,
    keysRef,
    selectedConversationRef,
    sendRealtimeCommand,
    notifyMessagesChanged,
  );

  useOutboxFlush(authenticated, conn, sendRealtimeCommand, notifyMessagesChanged, dispatchChat, selectedConversationRef);

  const conversationCapabilities = useMemo(
    () => conversationCapabilitiesFor(chatState.selectedConversation),
    [chatState.selectedConversation],
  );

  const value = useMemo(
    () => ({
      chatState,
      conversationCapabilities,
      dispatchChat,
      conversationLoading: chatState.conversationLoading,
      loadingOlder,
      selectedConversationRef,
      notifyMessagesChanged,
      loadPeerMessagesIntoState,
      loadOlderChatMessages,
      decodeServerMessageRow,
      mergeServerHistory,
      repairSync,
      forwardMessageToConversation,
      sendPlaintextMessage,
      sendChat,
      toggleReaction,
      deleteMessage,
      pickChatImage,
      pickChatFile,
      removeChatImage,
      retryFailedOutboxSend,
      sendVoiceMessage,
    }),
    [
      chatState,
      conversationCapabilities,
      chatState.conversationLoading,
      loadingOlder,
      notifyMessagesChanged,
      loadPeerMessagesIntoState,
      loadOlderChatMessages,
      decodeServerMessageRow,
      mergeServerHistory,
      repairSync,
      forwardMessageToConversation,
      sendPlaintextMessage,
      sendChat,
      toggleReaction,
      deleteMessage,
      pickChatImage,
      pickChatFile,
      removeChatImage,
      retryFailedOutboxSend,
      sendVoiceMessage,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat outside ChatProvider");
  return ctx;
}
